<template>
  <div class="border text-sm rounded divide-y overflow-hidden relative">
    <label class="block">
      <div class="px-4 py-3 spaxe-x-2">
        <input type="checkbox" :value="asset.id" v-model="localSelectedAssetIds" />
        <span class="inline-block ml-2">{{ title }}</span>
      </div>
    </label>
    <Assetable
      v-if="assetable"
      :without-actions="withoutActions"
      :type="asset.assetableType"
      :assetable="assetable"
      @view-note="$emit('view-note', assetable)"
    />
    <div v-else class="relative bg-gray-100" style="padding-bottom: 66.666667%">
      <div class="absolute w-full h-full inline-flex items-center justify-center">
        <div class="flex flex-col items-center space-y-4">Could not be found</div>
      </div>
    </div>
    <div class="px-4 py-3">tags: {{ tagsText ? tagsText : 'no tags' }}</div>
    <div v-if="!asset.accountRelationId">
      <div v-if="!confirmationMessageOpen" class="flex">
        <BaseButton ghost squared @click="editOpen = true" class="flex-1">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            class="inline-block w-4 h-4 focus:outline-none"
            v-tip="{ content: 'edit' }"
          >
            <path fill="none" d="M0 0h24v24H0z" />
            <path
              d="M6.414 16L16.556 5.858l-1.414-1.414L5 14.586V16h1.414zm.829
              2H3v-4.243L14.435 2.322a1 1 0 0 1 1.414 0l2.829 2.829a1
              1 0 0 1 0 1.414L7.243 18zM3 20h18v2H3v-2z"
            />
          </svg>
        </BaseButton>
        <portal-target :name="`${asset.assetableType}-${asset.assetableId}-actions`" slim />
        <BaseButton ghost squared @click="confirmDelete" class="flex-1">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            class="inline-block w-4 h-4 focus:outline-none"
            v-tip="{ content: 'delete' }"
          >
            <path fill="none" d="M0 0h24v24H0z" />
            <path
              d="M17 6h5v2h-2v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V8H2V6h5V3a1
              1 0 0 1 1-1h8a1 1 0 0 1 1 1v3zm1 2H6v12h12V8zM9 4v2h6V4H9z"
            />
          </svg>
        </BaseButton>
      </div>
      <div
        v-else
        class="absolute inset-0 bg-gray-900 bg-opacity-75 px-4 py-3 space-y-2 flex flex-col justify-center"
      >
        <BaseButton secondary :disabled="pendingDelete" @click="confirmationMessageOpen = false">
          Cancel
        </BaseButton>
        <BaseButton danger :disabled="pendingDelete || rejectedDelete" @click="tryDeleteAsset">
          {{ rejectedDelete ? 'Failed!' : 'Delete' }}
        </BaseButton>
      </div>
    </div>
    <portal v-if="!asset.accountRelationId" to="app-modal">
      <AccountLinkAssetUpdateModal
        v-if="editOpen && isLink"
        :asset="asset"
        @closed="editOpen = false"
      />
      <AccountNoteAssetUpdateModal
        v-if="editOpen && isNote"
        :asset="asset"
        @closed="editOpen = false"
      />
      <AccountMediaAssetUpdateModal
        v-if="editOpen && isMedia"
        :asset="asset"
        @closed="editOpen = false"
      />
    </portal>
  </div>
</template>

<script>
import AccountLinkAssetUpdateModal from '@/components/AccountAssets/AccountLinkAssetUpdateModal.vue';
import AccountNoteAssetUpdateModal from '@/components/AccountAssets/AccountNoteAssetUpdateModal.vue';
import AccountMediaAssetUpdateModal from '@/components/AccountAssets/AccountMediaAssetUpdateModal.vue';
import Assetable from './Assetable.vue';

export default {
  name: 'AssetTile',
  components: {
    Assetable,
    AccountLinkAssetUpdateModal,
    AccountNoteAssetUpdateModal,
    AccountMediaAssetUpdateModal,
  },
  props: {
    withoutActions: Boolean,
    asset: {
      type: Object,
      required: true,
    },
    selectedAssetIds: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      confirmationMessageOpen: false,
      pendingDelete: false,
      rejectedDelete: false,
      editOpen: false,
    };
  },
  computed: {
    localSelectedAssetIds: {
      get() {
        return this.selectedAssetIds;
      },
      set(value) {
        this.$emit('update:selected-asset-ids', value);
      },
    },
    title() {
      if (this.isLink) {
        return 'Link';
      }

      if (this.isNote) {
        return 'Note';
      }

      if (this.isMedia) {
        return 'Media';
      }

      return 'Unknown';
    },
    isLink() {
      return this.asset.assetableType === 'links';
    },
    isNote() {
      return this.asset.assetableType === 'notes';
    },
    isMedia() {
      return this.asset.assetableType === 'media';
    },
    assetable() {
      if (this.isLink) {
        return this.$store.state.entities.links.byId[this.asset.assetableId];
      }

      if (this.isNote) {
        return this.$store.state.entities.notes.byId[this.asset.assetableId];
      }

      if (this.isMedia) {
        return this.$store.state.entities.media.byId[this.asset.assetableId];
      }

      return null;
    },
    tags() {
      return (this.asset?.tags || []).map((tagId) => this.$store.state.entities.tags.byId[tagId]);
    },
    tagsText() {
      return this.tags.map((tag) => tag.name).join(', ');
    },
  },
  methods: {
    confirmDelete() {
      if (this.asset.accountRelationId) {
        return false;
      }
      this.confirmationMessageOpen = true;
      return true;
    },
    async tryDeleteAsset() {
      if (this.asset.accountRelationId) {
        return;
      }

      try {
        await this.$store.dispatch({
          type: 'ui/accountAssets/destroy',
          accountId: this.asset.accountId,
          assetId: this.asset.id,
        });
      } catch (error) {
        this.rejectedDelete = true;

        setTimeout(() => {
          this.rejectedDelete = false;
        }, 3000);
      }
    },
  },
};
</script>
